/**
 * A digitization whose validation found errors is a draft, not a result (spec
 * 2026-09-30-04 §5): the job wrote nothing and left the bundle on its output.
 * This dialog shows it — a thumbnail with the failing rooms in red, and the
 * issues grouped by kind — and lets the user accept it whole, keep only the
 * rooms that passed, or discard it.
 */
import { FLOORPLAN_ISSUE_CODES } from './engine/validate'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { Button } from '../ui/button'
import { PlanPreviewThumbnail } from './PlanPreviewThumbnail'
import type { DigitizeOutput } from './useDigitizePlan'

export type DigitizeKeep = 'all' | 'valid'

interface Props {
    output: DigitizeOutput | null
    busy: boolean
    error: string | null
    onAccept: (keep: DigitizeKeep) => void
    onDiscard: () => void
}

export function DigitizePreviewDialog({ output, busy, error, onAccept, onDiscard }: Props) {
    const { t } = useTranslation('assets')
    const preview = output?.preview
    const spaces = useMemo(() => preview?.value?.spaces ?? [], [preview])
    const broken = useMemo(() => new Set(preview?.broken_space_ids ?? []), [preview])
    const validCount = spaces.filter((sp) => !broken.has(sp.id)).length

    // Issues grouped by code, errors first, each with the rooms it names.
    const groups = useMemo(() => {
        const names = new Map(spaces.map((sp) => [sp.id, sp.name || sp.id]))
        const byCode = new Map<string, { code: string; severity: string; count: number; rooms: string[] }>()
        for (const is of output?.validation?.issues ?? []) {
            const g = byCode.get(is.code) ?? { code: is.code, severity: is.severity, count: 0, rooms: [] }
            g.count++
            const room = is.element_id ? names.get(is.element_id) : undefined
            if (room && !g.rooms.includes(room)) g.rooms.push(room)
            byCode.set(is.code, g)
        }
        return [...byCode.values()].sort((a, b) =>
            (a.severity === 'error' ? 0 : 1) - (b.severity === 'error' ? 0 : 1) || b.count - a.count)
    }, [output, spaces])

    return (
        <AlertDialog open={!!preview}>
            <AlertDialogContent data-testid="plan-editor-digitize-preview" className="max-w-xl">
                <AlertDialogHeader>
                    <AlertDialogTitle>{t('planEditor.digitizePreview.title')}</AlertDialogTitle>
                    <AlertDialogDescription>
                        {t('planEditor.digitizePreview.body', {
                            count: output?.validation?.errors ?? 0,
                            spaces: spaces.length,
                            valid: validCount,
                        })}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <PlanPreviewThumbnail spaces={spaces} broken={broken} testId="plan-editor-digitize-preview-thumbnail" />
                <ul className="max-h-48 space-y-1 overflow-y-auto text-xs" data-testid="plan-editor-digitize-preview-issues">
                    {groups.map((g) => (
                        <li
                            key={g.code}
                            className={g.severity === 'error' ? 'text-destructive' : 'text-muted-foreground'}
                            data-testid={`plan-editor-digitize-preview-issue-${g.code}`}
                        >
                            <span className="font-medium">
                                {(FLOORPLAN_ISSUE_CODES as readonly string[]).includes(g.code)
                                    ? t(`planEditor.floorplanIssues.${g.code}`)
                                    : g.code}
                            </span>
                            {' '}× {g.count}
                            {g.rooms.length > 0 && (
                                <span className="text-muted-foreground"> — {g.rooms.slice(0, 8).join(', ')}{g.rooms.length > 8 ? '…' : ''}</span>
                            )}
                        </li>
                    ))}
                </ul>
                {error && (
                    <p className="text-xs text-destructive" data-testid="plan-editor-digitize-preview-error">{error}</p>
                )}
                <AlertDialogFooter className="gap-2 sm:gap-2">
                    <AlertDialogCancel
                        data-testid="plan-editor-digitize-preview-discard"
                        disabled={busy}
                        onClick={onDiscard}
                    >
                        {t('planEditor.digitizePreview.discard')}
                    </AlertDialogCancel>
                    {broken.size > 0 && (
                        <Button
                            variant="outline"
                            data-testid="plan-editor-digitize-preview-keep-valid"
                            disabled={busy || validCount === 0}
                            title={validCount === 0 ? t('planEditor.digitizePreview.nothing_valid') : undefined}
                            onClick={() => onAccept('valid')}
                        >
                            {t('planEditor.digitizePreview.keep_valid', { count: validCount })}
                        </Button>
                    )}
                    <Button
                        data-testid="plan-editor-digitize-preview-accept"
                        disabled={busy}
                        onClick={() => onAccept('all')}
                    >
                        {t('planEditor.digitizePreview.accept')}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}
